import React, { useEffect, useRef } from 'react';
import { Filter } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const thBase =
  'px-6 py-3 font-semibold text-sm whitespace-nowrap align-middle min-w-[88px]';

/**
 * Double-click column header to filter — ported from Qawaem Console TableColumnHeaderFilter.
 */
export default function TableColumnHeaderFilter({
  columnKey,
  label,
  kind = 'text',
  value = '',
  onChange,
  options = [],
  className = '',
  align = 'start',
  editingKey,
  onEditingKeyChange,
  thClassName = '',
}) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const selectRef = useRef(null);
  const isEditing = editingKey === columnKey;
  const hasValue = Boolean(String(value).trim());
  const alignClass = align === 'center' ? 'text-center' : 'text-start';

  useEffect(() => {
    if (!isEditing) return;
    if (kind === 'select') selectRef.current?.focus();
    else inputRef.current?.focus();
  }, [isEditing, kind]);

  const closeEditor = () => onEditingKeyChange(null);
  const allLabel = t('common.all', { defaultValue: 'الكل' });
  const dblClickHint = t('common.column_filter_hint', { defaultValue: 'انقر مرتين للبحث في هذا العمود' });
  const searchPlaceholder = t('common.column_filter_search', { defaultValue: 'بحث: {{label}}', label });

  const labelRowClass =
    align === 'center'
      ? 'flex items-center justify-center gap-1.5 min-h-[1.75rem] w-full max-w-full'
      : 'flex items-center gap-1.5 min-h-[1.75rem] max-w-full';

  if (isEditing) {
    return (
      <th className={`${thBase} ${alignClass} ${thClassName} ${className}`}>
        {kind === 'select' ? (
          <select
            ref={selectRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={closeEditor}
            className="column-header-filter-control w-full min-w-[120px]"
          >
            <option value="">{allLabel}</option>
            {options.map((opt) => (
              <option key={opt.value || opt.label} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            ref={inputRef}
            type="search"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={closeEditor}
            onKeyDown={(e) => {
              if (e.key === 'Escape') closeEditor();
            }}
            placeholder={searchPlaceholder}
            className="column-header-filter-control w-full min-w-[120px]"
          />
        )}
      </th>
    );
  }

  return (
    <th
      className={`${thBase} ${alignClass} cursor-pointer select-none group ${hasValue ? 'column-header-filter-active' : ''} ${thClassName} ${className}`}
      onDoubleClick={() => onEditingKeyChange(columnKey)}
      title={dblClickHint}
    >
      <div className={labelRowClass}>
        <span className="truncate">{label}</span>
        {hasValue ? (
          <Filter className="h-3 w-3 shrink-0 text-[#6e9b6e]" aria-hidden />
        ) : (
          <span className="text-[10px] text-console-subtle opacity-0 group-hover:opacity-100 transition-opacity shrink-0 leading-none">
            ⇧×2
          </span>
        )}
      </div>
    </th>
  );
}
